/* eslint-disable no-param-reassign, no-underscore-dangle, unicorn/consistent-destructuring, func-names */
import angular from 'angular';
import _ from 'lodash-es';
import Defaults from '~/scripts/constants/defaults';
import notificationTemplate from '~/templates/notifications/common.html';

/* globals Knitiv */

/**
 * @typedef {'debug'
 *   | 'info'
 *   | 'notice'
 *   | 'warning'
 *   | 'error'
 *   | 'critical'
 *   | 'alert'
 *   | 'emergency'
 * } RFC5242Levels
 */

/**
 * @typedef {'primary'
 *   | 'info'
 *   | 'warning'
 *   | 'danger'
 *   | 'success'
 * } ColorTheme
 */

const priorityCompareTop = (
	/** @type {any} */ a,
	/** @type {any} */ b,
) => a._priority - b._priority;

const priorityCompareBtm = (
	/** @type {any} */ a,
	/** @type {any} */ b,
) => b._priority - a._priority;

/**
 * @typedef {Object} NotificationOptions
 * @property {'primary' | 'error' | 'success' | 'info' | 'warning'} [type]
 * @property {string} [title]
 * @property {string} [message]
 * @property {number} [delay]
 * @property {string} [template]
 */

/**
 * @typedef {function(NotificationOptions|string, string=): any} NotificationCallable
 */

/**
 * @typedef {NotificationCallable & {
 *   primary: (args: NotificationOptions|string) => any,
 *   error: (args: NotificationOptions|string) => any,
 *   success: (args: NotificationOptions|string) => any,
 *   info: (args: NotificationOptions|string) => any,
 *   warning: (args: NotificationOptions|string) => any,
 *   clearAll: () => any,
 * }} NotificationService
 */

/**
 * @param {RFC5242Levels | ColorTheme} type
 * @returns {ColorTheme}
 */
const type2Class = type => {
	switch (type) {
	case 'debug': {
		return 'primary';
	}
	case 'notice': {
		return 'info';
	}
	case 'alert':
	case 'critical':
	case 'emergency':
	case 'error': {
		return 'danger';
	}
	default: {
		return type;
	}
	}
}

export class NotificationProvider {
	/* @ngInject */
	constructor() {
		this.options = {
			delay:             5000,
			startTop:          10,
			startRight:        10,
			verticalSpacing:   10,
			horizontalSpacing: 10,
			positionX:         'right',
			positionY:         'top',
			replaceMessage:    false,
			template:          notificationTemplate,
			type:              undefined,
			onClose:           undefined,
			onClick:           undefined,
			closeOnClick:      true,
			maxCount:          0, // 0 - Infinite
			container:         'body',
			priority:          10,
			addToHistory:      true,
		};

		this.setOptions = (/** @type {any} */ options) => {
			if (!angular.isObject(options)) {
				throw new TypeError('Options should be an object!');
			}
			this.options = _.merge({}, this.options, options);
		};
	}

	/* @ngInject */
	/**
	 * @param {import('angular').ICompileService} $compile
	 * @param {import('angular').ILocationService} $location
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').ISCEService} $sce
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').IWindowService} $window
	 * @returns {any}
	 */
	$get(
		$compile,
		$location,
		$rootScope,
		$sce,
		$timeout,
		$window,
	) {
		const { options } = this;
		const { startTop } = options;
		const { startRight } = options;
		const { verticalSpacing } = options;
		const { horizontalSpacing } = options;
		const { delay } = options;

		/** @type {any[]} */
		const messageElements = [];

		$rootScope.notificationHistory = [];

		let isResizeBound = false;

		/**
		 *
		 * @param {any} arguments_
		 * @param {string} t
		 * @returns {Promise<any>}
		 */
		const notify = async (arguments_, t) => {
			let returnValue = false;

			if (typeof arguments_ !== 'object' || arguments_ === null) {
				arguments_ = { message: arguments_ };
			}

			arguments_.scope = arguments_.scope ?? $rootScope;
			arguments_.template = arguments_.template ?? options.template;
			arguments_.delay = angular.isUndefined(arguments_.delay) ? delay : arguments_.delay;
			arguments_.type = t || arguments_.type || options.type || '';
			arguments_.class = type2Class(arguments_.type);
			arguments_.positionY = arguments_.positionY ?? options.positionY;
			arguments_.positionX = arguments_.positionX ?? options.positionX;
			arguments_.replaceMessage = arguments_.replaceMessage ?? options.replaceMessage;
			arguments_.onClose = arguments_.onClose ?? options.onClose;
			arguments_.onClick = arguments_.onClick ?? options.onClick;
			arguments_.closeOnClick = (arguments_.closeOnClick !== null && arguments_.closeOnClick !== undefined)
				? arguments_.closeOnClick : options.closeOnClick;
			arguments_.container = arguments_.container ?? options.container;
			arguments_.priority = arguments_.priority ?? options.priority;
			arguments_.timestamp = new Date();
			arguments_.closed = false;
			arguments_.addToHistory ??= options.addToHistory;

			if (arguments_.addToHistory) {
				const addedCount = $rootScope.notificationHistory.unshift(arguments_);

				if (
					addedCount > Defaults.maxNotifications
				) {
					$rootScope.notificationHistory.length = Defaults.maxNotifications;
				}
			}

			const processNotificationTemplate = (/** @type {string} */ template) => {
				const scope = arguments_.scope.$new();
				scope.host = $location.host();
				scope.message = $sce.trustAsHtml(arguments_.message);
				scope.title = $sce.trustAsHtml(arguments_.title);
				scope.t = arguments_.type.slice(0, 1);
				scope.delay = arguments_.delay;
				scope.onClose = arguments_.onClose;
				scope.onClick = arguments_.onClick;

				const reposite = () => {
					let index = 0;
					let k = 0;
					const lastRight = startRight;
					const lastPosition = [];

					if (arguments_.positionY === 'top') {
						messageElements.sort(priorityCompareTop);
					}
					else if (arguments_.positionY === 'bottom') {
						messageElements.sort(priorityCompareBtm);
					}

					for (let index_ = messageElements.length - 1; index_ >= 0; index_--) {
						const element = messageElements[index_];
						if (arguments_.replaceMessage && index_ < messageElements.length - 1) {
							element.addClass('killed');
							// eslint-disable-next-line no-continue
							continue;
						}
						const elementHeight = Number.parseInt(element[0].offsetHeight, 10);
						const
							elementWidth = Number.parseInt(element[0].offsetWidth, 10);

						/** @type {number} */
						let position = lastPosition[element._positionY + element._positionX];
						let top = 0;

						if ((top + elementHeight) > window.innerHeight) {
							position = startTop;
							k++;
							index = 0;
						}

						top = (position ? (index === 0 ? position : position + verticalSpacing) : startTop);
						const right = lastRight + (k * (horizontalSpacing + elementWidth));

						element.css(element._positionY, `${top}px`);
						if (element._positionX === 'center') {
							element.css('left', `${window.innerWidth / 2 - elementWidth / 2}px`);
						}
						else {
							element.css(element._positionX, `${right}px`);
						}

						lastPosition[element._positionY + element._positionX] = top + elementHeight;

						if (options.maxCount > 0 && messageElements.length > options.maxCount && index_ === 0) {
							element.scope()
								.kill(true);
						}

						index++;
					}
				};

				const templateElement = $compile(template)(scope);
				// @ts-ignore
				templateElement._positionY = arguments_.positionY;
				// @ts-ignore
				templateElement._positionX = arguments_.positionX;
				// @ts-ignore
				templateElement._priority = arguments_.priority;
				templateElement.addClass(arguments_.type);

				/**
				 * @param {any} event
				 */
				const closeEvent = event => {
					event = event.originalEvent || event;
					if (event.type === 'click' || (event.propertyName === 'opacity' && event.elapsedTime >= 1)) {
						if (scope.onClose) {
							scope.$apply(scope.onClose(templateElement));
						}

						if (event.type === 'click' && scope.onClick) {
							scope.$apply(scope.onClick(templateElement));
						}

						templateElement.remove();
						messageElements.splice(messageElements.indexOf(templateElement), 1);
						arguments_.closed = true;
						scope.$destroy();
						reposite();
					}
				};

				if (arguments_.closeOnClick) {
					templateElement.addClass('clickable');
					templateElement.bind('click', closeEvent);
				}

				templateElement.bind(
					'webkitTransitionEnd oTransitionEnd otransitionend transitionend msTransitionEnd',
					closeEvent,
				);

				if (angular.isNumber(arguments_.delay)) {
					$timeout(() => {
						templateElement.addClass('killed');
					}, arguments_.delay);
				}

				/**
				 * @param {string} value
				 */
				const setCssTransitions = value => {
					for (const prefix of [
						'-webkit-transition',
						'-o-transition',
						'transition',
					]) {
						templateElement.css(prefix, value);
					}
				};

				setCssTransitions('none');

				angular.element(document.querySelector(arguments_.container))
					.append(templateElement);
				const offset = -(templateElement[0].offsetHeight + 50);
				// @ts-ignore
				templateElement.css(templateElement._positionY, `${offset}px`);
				messageElements.push(templateElement);

				if (arguments_.positionX === 'center') {
					const elementWidth = templateElement[0].offsetWidth;
					templateElement.css('left', `${window.innerWidth / 2 - elementWidth / 2}px`);
				}

				$timeout(() => {
					setCssTransitions('');
				});

				scope._templateElement = templateElement;

				scope.kill = (/** @type {boolean} */ isHard) => {
					if (isHard) {
						if (scope.onClose) {
							scope.$apply(scope.onClose(scope._templateElement));
						}

						messageElements.splice(messageElements.indexOf(scope._templateElement), 1);
						arguments_.closed = true;
						scope._templateElement.remove();
						scope.$destroy();
						$timeout(reposite);
					}
					else {
						scope._templateElement.addClass('killed');
					}
				};

				$timeout(reposite);

				if (!isResizeBound) {
					angular.element($window)
						.bind('resize', () => {
							$timeout(reposite);
						});
					isResizeBound = true;
				}

				returnValue = scope;
			};

			processNotificationTemplate(arguments_.template);

			return returnValue;
		};

		/**
		 *
		 * @param {any} arguments_
		 * @returns {any}
		 */
		notify.primary = function (arguments_) {
			return this(arguments_, 'primary');
		};

		/**
		 *
		 * @param {any} arguments_
		 * @returns {any}
		 */
		notify.error = function (arguments_) {
			return this(arguments_, 'error');
		};
		/**
		 *
		 * @param {any} arguments_
		 * @returns {any}
		 */
		notify.success = function (arguments_) {
			return this(arguments_, 'success');
		};
		/**
		 *
		 * @param {any} arguments_
		 * @returns {any}
		 */
		notify.info = function (arguments_) {
			return this(arguments_, 'info');
		};
		/**
		 *
		 * @param {any} arguments_
		 * @returns {any}
		 */
		notify.warning = function (arguments_) {
			return this(arguments_, 'warning');
		};

		notify.clearAll = () => {
			for (const element of messageElements) {
				element.addClass('killed');
			}
		};

		return notify;
	}
}
